import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import TopHeaderBar from './components/v2/TopHeaderBar';
import LeftToolbar from './components/v2/LeftToolbar';
import CanvasBoardArea from './components/v2/CanvasBoardArea';
import RightSidebar from './components/v2/RightSidebar';
import { useSelector, useDispatch } from 'react-redux';
import { setProjectName, setCanvasId, loadProject } from './redux/slices/canvasSlice';
import { readCanvasConfig } from '../services/s3Service';
import axios from 'axios';

/**
 * Canvas Editor V2 - Redesigned Layout
 * 
 * Layout Structure:
 * - Top: Fixed header bar with controls and logo
 * - Left: Vertical toolbar with shape/element tools
 * - Center: Main canvas board area
 * - Right: Properties and layers sidebar
 */
const CanvasEditorV2 = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { projectName } = useSelector((state) => state.canvas || { projectName: 'Untitled Menu' });
  const { takeawayList } = useSelector((state) => state.auth);
  
  const [activeToolbarTab, setActiveToolbarTab] = useState(null);
  const [showCanvasPropertiesModal, setShowCanvasPropertiesModal] = useState(false);
  const [storeId, setStoreId] = useState(null);
  const [apiToken, setApiToken] = useState(null);
  const [syncMenu, setSyncMenu] = useState(1);
  const [menuVersion, setMenuVersion] = useState('v1');
  const [loading, setLoading] = useState(true);

  /**
   * Check menu version by fetching v2MenuClients list
   * Uses a CORS proxy if direct access fails
   */
  const checkMenuVersion = async (storeId) => {
    if (!storeId) return 'v1';
    
    const url = 'https://digisolutions-assets.s3.eu-west-1.amazonaws.com/slideconfig/config/v2MenuClients.json';
    
    try {
      // Try direct fetch first
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
      });
      
      if (response.ok) {
        const data = await response.json();
        const isV2 = data?.v2MenuClients?.includes(storeId);
        return isV2 ? 'v2' : 'v1';
      }
    } catch (error) {
      console.warn('Direct fetch failed, trying CORS proxy:', error);
      
      // Fallback: Try using a CORS proxy
      try {
        // Using allorigins.win as a CORS proxy
        const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`;
        const proxyResponse = await fetch(proxyUrl);
        
        if (proxyResponse.ok) {
          const proxyData = await proxyResponse.json();
          const data = JSON.parse(proxyData.contents);
          const isV2 = data?.v2MenuClients?.includes(storeId);
          return isV2 ? 'v2' : 'v1';
        }
      } catch (proxyError) {
        console.warn('CORS proxy also failed:', proxyError);
      }
    }
    
    // Default to v1 if all methods fail
    console.warn('Unable to check menu version, defaulting to v1');
    return 'v1';
  };

  useEffect(() => {
    const initializeCanvas = async () => {
      try {
        // Get project data from location state
        const { projectName: locationProjectName, branchId, canvasId } = location.state || {};
        
        // If canvasId is provided, load the canvas data from S3
        if (canvasId && branchId) {
          try {
            const branch = takeawayList?.find(
              (b) => (b.store_id || b.id) === branchId
            );
            const storeId = branch?.store_id || branch?.id || branchId;
            
            const canvases = await readCanvasConfig(storeId);
            const canvasData = canvases.find(c => c.id === canvasId);
            
            if (canvasData) {
              // Load the canvas data into Redux
              dispatch(loadProject(canvasData));
              dispatch(setCanvasId(canvasId));
            }
          } catch (error) {
            console.error('Error loading canvas data:', error);
          }
        }
        
        // Set project name in Redux
        if (locationProjectName) {
          dispatch(setProjectName(locationProjectName));
        }

        // Find branch from takeawayList
        if (branchId && takeawayList && takeawayList.length > 0) {
          const branch = takeawayList.find(
            (b) => (b.store_id || b.id) === branchId
          );

          if (branch) {
            const branchStoreId = branch.store_id || branch.id;
            const branchApiToken = branch.license_key || branch.apiToken;
            const branchSyncMenu = branch.syncMenu || 1;

            setStoreId(branchStoreId);
            setApiToken(branchApiToken);
            setSyncMenu(branchSyncMenu);
            console.log('branchStoreId', branchStoreId);
            console.log('branchApiToken', branchApiToken);
            console.log('branchSyncMenu', branchSyncMenu);

            // Check menu version
            const version = await checkMenuVersion(branchStoreId);
            setMenuVersion(version);
          }
        }
      } catch (error) {
        console.error('Error initializing canvas:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeCanvas();
  }, [location.state, takeawayList, dispatch]);

  if (loading) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#F5F5F5',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#F5F5F5',
      }}
    >
      {/* Top Header Bar */}
      <TopHeaderBar projectName={projectName} storeId={storeId} />

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        {/* Left Toolbar */}
        <LeftToolbar
          activeTab={activeToolbarTab}
          onTabChange={setActiveToolbarTab}
          storeId={storeId}
          apiToken={apiToken}
          syncMenu={syncMenu}
          menuVersion={menuVersion}
        />

        {/* Canvas Board Area (Center) */}
        <CanvasBoardArea
          showCanvasPropertiesModal={showCanvasPropertiesModal}
          onToggleCanvasProperties={() => setShowCanvasPropertiesModal(!showCanvasPropertiesModal)}
        />

        {/* Right Sidebar */}
        <RightSidebar />
      </Box>
    </Box>
  );
};

export default CanvasEditorV2;

